import '@fontsource-variable/dm-sans';
import '@fontsource/dm-mono';
import '@fontsource-variable/playfair-display';
import './app/globals.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';

import { AppHead } from '@/components/AppHead';
import { BuildBadge } from '@/components/BuildBadge';
import { IosZoomGuard } from '@/components/IosZoomGuard';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { PrivacyProvider } from '@/contexts/PrivacyContext';
import { UpgradeRequiredDialog } from '@/components/UpgradeRequiredDialog';
import { TelemetryProvider, trackException } from '@/lib/telemetry';
import { ThemeProvider } from '@/lib/theme/ThemeProvider';
import { initThemeMode } from '@/lib/theme/theme-mode';
import { allowedRedirectOrigins } from '@/lib/auth-redirect';
import { CLERK_PUBLISHABLE_KEY } from '@/lib/env';
import { installChunkErrorRecovery } from '@/lib/chunk-reload';
import { router } from './router';

if (!CLERK_PUBLISHABLE_KEY) {
  console.error('Missing VITE_CLERK_PUBLISHABLE_KEY');
}

// Apply the persisted theme before first paint so there's no flash of the wrong theme.
initThemeMode();

// A deploy replaces the hashed chunks; tabs still running the previous index.html fail
// their next lazy import ("Failed to fetch dynamically imported module"). Clerk's own
// lazily-loaded chunks fail the same way but report it as a webpack ChunkLoadError,
// outside Vite's event and outside the router. Recover from all of them.
//
// `report` names the failing URL. Cross-origin scripts — clerk-js's chunks above all —
// have their exceptions censored to a bare "Script error." with no stack, so the load
// failure is the only place the file is still identifiable.
installChunkErrorRecovery({ report: trackException });

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppHead />
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl="/login"
      signUpUrl="/register"
      // The same origins the auth layout follows `redirect_url` to: <SignIn> sends a
      // freshly signed-in user back to Clerk's OAuth authorize endpoint (MCP connectors).
      allowedRedirectOrigins={allowedRedirectOrigins(window.location.origin, CLERK_PUBLISHABLE_KEY)}
      appearance={{
        variables: {
          colorPrimary: '#0EA5A8',
          borderRadius: '0.75rem',
          fontFamily: 'var(--font-sans), system-ui, sans-serif',
        },
      }}
    >
      <TelemetryProvider>
        <ThemeProvider>
          <TooltipProvider>
            <PrivacyProvider>
              <IosZoomGuard />
              <RouterProvider router={router} />
              <UpgradeRequiredDialog />
              <Toaster />
              <BuildBadge />
            </PrivacyProvider>
          </TooltipProvider>
        </ThemeProvider>
      </TelemetryProvider>
    </ClerkProvider>
  </StrictMode>,
);
